import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, MenuItem
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import PurchaseLoader from '../../common/PurchaseLoader';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY_HEADER = {
  PurchaseBillNumber: '', PurchaseDate: today(),
  SupplierId: null, SupplierName: '', GSTNumber: '',
  Address: '', Description: '',
  Amount: 0, DiscountAmount: 0, OtherAmount: 0,
  CGST: 0, SGST: 0, IGST: 0,
  Roundoff: 0, TotalAmount: 0,
  PaidAmount: 0, CardAmount: 0, UpiAmount: 0,
  BalanceAmount: 0, PaymentBankId: null,
};

const EMPTY_DETAIL = {
  ItemId: null, ItemName: '', HSNCode: '', GSTpercent: 0,
  Quantity: 1, Rate: 0, Amount: 0,
  Discount: 0, TotalAmount: 0,
  CGST: 0, SGST: 0, IGST: 0,
};

// ─── GST Calculation Helper ─────────────────────────────────────────────────
function calcRowGST(row, gstCalcType, isInterState = false) {
  const qty = parseFloat(row.Quantity) || 0;
  const rate = parseFloat(row.Rate) || 0;
  const gstRate = parseFloat(row.GSTpercent) || 0;
  const discount = parseFloat(row.Discount) || 0;

  const amount = Math.round(qty * rate * 100) / 100;
  const discountAmount = Math.round(amount * discount / 100 * 100) / 100;
  const taxableAmount = amount - discountAmount;

  let cgst = 0, sgst = 0, igst = 0;

  if (isInterState) {
    igst = Math.round(taxableAmount * gstRate / 100 * 100) / 100;
  } else {
    cgst = Math.round(taxableAmount * (gstRate / 2) / 100 * 100) / 100;
    sgst = Math.round(taxableAmount * (gstRate / 2) / 100 * 100) / 100;
  }

  const totalAmount = Math.round((taxableAmount + cgst + sgst + igst) * 100) / 100;

  return {
    Amount: amount,
    CGST: cgst,
    SGST: sgst,
    IGST: igst,
    TotalAmount: totalAmount,
  };
}

// ─── Component ──────────────────────────────────────────────────────────────
const PurchaseModal = ({ open, onClose, onSave, purchaseData = null, mode = 'create' }) => {
  const { companyId, branchId, userId, token, branchstate } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [formData, setFormData] = useState(EMPTY_HEADER);
  const [errors, setErrors] = useState({});
  const [details, setDetails] = useState([]);

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const isViewMode = mode === 'view';
  const isReadOnly = mode === 'view';

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load lookups (suppliers, items)
  useEffect(() => {
    if (!open || lookupsLoaded || !companyId || !token) return;
    const load = async () => {
      try {
        const [suppRes, itemRes, bankRes] = await Promise.all([
          fetchData('MasterApi', `/${companyId}`, token),
          fetchData('ItemApi', `/${companyId}`, token),
          fetchData('BankApi', `/${companyId}`, token),
        ]);
        if (suppRes?.responseCode === '1111' && suppRes.response_data)
          setSuppliers(Array.isArray(suppRes.response_data) ? suppRes.response_data : [suppRes.response_data]);
        if (itemRes?.responseCode === '1111' && itemRes.response_data)
          setItems(Array.isArray(itemRes.response_data) ? itemRes.response_data : [itemRes.response_data]);
        if (bankRes?.responseCode === '1111' && bankRes.response_data)
          setBanks(Array.isArray(bankRes.response_data) ? bankRes.response_data : [bankRes.response_data]);
      } catch (_) {}
      setLookupsLoaded(true);
    };
    load();
  }, [open, lookupsLoaded, companyId, token]);

  // Load form data on open
  useEffect(() => {
    if (!open) return;
    if (purchaseData && mode !== 'create') {
      setLoading(true);
      fetchData('PurchaseApi', `/${purchaseData.Id}`, token)
        .then(r => {
          const d = (r && r.responseCode === '1111' && r.response_data) ? r.response_data : null;
          if (d) {
            const p = d.Purchase || d;
            setFormData({
              PurchaseBillNumber: p.PurchaseBillNumber || '',
              PurchaseDate: p.PurchaseDate ? p.PurchaseDate.split('T')[0] : today(),
              SupplierId: p.SupplierId || null,
              SupplierName: p.SupplierName || '',
              GSTNumber: p.GSTNumber || '',
              Address: p.Address || '',
              Description: p.Description || '',
              Amount: p.Amount || 0,
              DiscountAmount: p.DiscountAmount || 0,
              OtherAmount: p.OtherAmount || 0,
              CGST: p.CGST || 0,
              SGST: p.SGST || 0,
              IGST: p.IGST || 0,
              Roundoff: p.Roundoff || 0,
              TotalAmount: p.TotalAmount || 0,
              PaidAmount: p.PaidAmount || 0,
              CardAmount: p.CardAmount || 0,
              UpiAmount: p.UpiAmount || 0,
              BalanceAmount: p.BalanceAmount || 0,
              PaymentBankId: p.PaymentBankId || null,
            });
            const det = d.PurchaseDetails || [];
            setDetails(det.map(dd => ({
              Id: dd.Id || 0,
              ItemId: dd.ItemId,
              ItemName: dd.ItemName || '',
              HSNCode: dd.HSNCode || '',
              GSTpercent: dd.GSTpercent || 0,
              Quantity: dd.Quantity || 0,
              Rate: dd.Rate || 0,
              Amount: dd.Amount || 0,
              Discount: dd.Discount || 0,
              TotalAmount: dd.TotalAmount || 0,
              CGST: dd.CGST || 0,
              SGST: dd.SGST || 0,
              IGST: dd.IGST || 0,
            })));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      // Create mode
      setFormData(EMPTY_HEADER);
      setDetails([]);
      // Fetch max bill number
      if (companyId && branchId && token) {
        fetchData('PurchaseApi', `/MaxBillno/${branchId}/${companyId}`, token)
          .then(r => {
            const data = r && (r.response_data ?? r.responseData ?? r);
            const next = (parseInt(data?.BillNumber ?? data?.OrderNumber ?? data) || 0) + 1;
            setFormData(prev => ({ ...prev, PurchaseBillNumber: next }));
          })
          .catch(() => {});
      }
    }
    setErrors({});
  }, [open, purchaseData, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine inter-state based on supplier GST and branch state
  const isInterState = useCallback(() => {
    const supplierGST = formData.GSTNumber || '';
    if (!supplierGST || supplierGST.length < 2 || !branchstate) return false;
    const supplierStateCode = supplierGST.substring(0, 2);
    const branchStateCode = branchstate.substring(0, 2);
    return supplierStateCode !== branchStateCode;
  }, [formData.GSTNumber, branchstate]);

  // Recalculate header totals whenever details change
  useEffect(() => {
    const totalAmount = details.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0);
    const totalDiscount = details.reduce((s, d) => s + (parseFloat(d.Amount) * (parseFloat(d.Discount) || 0) / 100 || 0), 0);
    const totalCGST = details.reduce((s, d) => s + (parseFloat(d.CGST) || 0), 0);
    const totalSGST = details.reduce((s, d) => s + (parseFloat(d.SGST) || 0), 0);
    const totalIGST = details.reduce((s, d) => s + (parseFloat(d.IGST) || 0), 0);
    const totalItemsTotal = details.reduce((s, d) => s + (parseFloat(d.TotalAmount) || 0), 0);

    const other = parseFloat(formData.OtherAmount) || 0;
    const subTotal = totalItemsTotal + other;
    const roundoff = Math.round(subTotal) - subTotal;
    const grandTotal = Math.round(subTotal + roundoff);
    const paid = parseFloat(formData.PaidAmount) || 0;
    const card = parseFloat(formData.CardAmount) || 0;
    const upi = parseFloat(formData.UpiAmount) || 0;
    const balance = grandTotal - paid - card - upi;

    setFormData(prev => ({
      ...prev,
      Amount: Math.round(totalAmount * 100) / 100,
      DiscountAmount: Math.round(totalDiscount * 100) / 100,
      CGST: Math.round(totalCGST * 100) / 100,
      SGST: Math.round(totalSGST * 100) / 100,
      IGST: Math.round(totalIGST * 100) / 100,
      Roundoff: Math.round(roundoff * 100) / 100,
      TotalAmount: grandTotal,
      BalanceAmount: Math.round(balance * 100) / 100,
    }));
  }, [details, formData.OtherAmount, formData.PaidAmount, formData.CardAmount, formData.UpiAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Header field change handler ────────────────────────────────────────────
  const handleFieldChange = (field, value) => {
    const paymentFields = ['PaidAmount', 'CardAmount', 'UpiAmount'];
    if (paymentFields.includes(field)) {
      const newVal = parseFloat(value) || 0;
      if (newVal < 0) return;
      setFormData(prev => {
        const updated = { ...prev, [field]: value };
        const paid  = parseFloat(field === 'PaidAmount'  ? value : prev.PaidAmount)  || 0;
        const card  = parseFloat(field === 'CardAmount'  ? value : prev.CardAmount)  || 0;
        const upi   = parseFloat(field === 'UpiAmount'   ? value : prev.UpiAmount)   || 0;
        const total = parseFloat(prev.TotalAmount) || 0;
        const balance = parseFloat((total - paid - card - upi).toFixed(2));
        if (balance < 0) return prev;
        return { ...updated, BalanceAmount: balance };
      });
      if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // ── Supplier selection ─────────────────────────────────────────────────────
  const handleSupplierChange = (_, supplier) => {
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        SupplierId: supplier.Id,
        SupplierName: supplier.Name || '',
        GSTNumber: supplier.GSTNumber || '',
        Address: supplier.Address || supplier.AddressLine1 || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        SupplierId: null, SupplierName: '', GSTNumber: '', Address: '',
      }));
    }
    if (errors.SupplierId) setErrors(prev => ({ ...prev, SupplierId: null }));
  };

  // ── Detail row handlers ────────────────────────────────────────────────────
  const handleAddRow = () => {
    setDetails(prev => [...prev, { ...EMPTY_DETAIL }]);
  };

  const handleRemoveRow = (index) => {
    setDetails(prev => prev.filter((_, i) => i !== index));
  };

  const handleDetailChange = (index, field, value) => {
    setDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Recalculate row GST when quantity, rate, GST rate, or discount changes
      if (['Quantity', 'Rate', 'GSTpercent', 'Discount'].includes(field)) {
        const calc = calcRowGST(updated[index], formData.GstCalculationType, isInterState());
        updated[index] = { ...updated[index], ...calc };
      }
      return updated;
    });
  };

  const handleItemSelect = (index, item) => {
    if (item) {
      setDetails(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          ItemId: item.Id,
          ItemName: item.Name || '',
          HSNCode: item.HSNcode || item.HSNCode || '',
          GSTpercent: item.GSTpercent || 0,
          Rate: item.PurchaseRate || 0,
        };
        // Recalculate
        const calc = calcRowGST(updated[index], 1, isInterState());
        updated[index] = { ...updated[index], ...calc };
        return updated;
      });
    }
  };

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.SupplierId) errs.SupplierId = 'Supplier is required';
    if (!formData.PurchaseBillNumber) errs.PurchaseBillNumber = 'Bill number is required';
    if (!formData.PurchaseDate) errs.PurchaseDate = 'Date is required';
    if (details.length === 0) errs.details = 'At least one item is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        Purchase: {
          Id: purchaseData?.Id || 0,
          CompanyId: companyId,
          SupplierId: formData.SupplierId,
          SupplierName: formData.SupplierName,
          PurchaseDate: formData.PurchaseDate,
          PurchaseBillNumber: String(formData.PurchaseBillNumber),
          Address: formData.Address,
          GSTNumber: formData.GSTNumber,
          Description: formData.Description,
          Amount: formData.Amount,
          DiscountAmount: formData.DiscountAmount,
          OtherAmount: parseFloat(formData.OtherAmount) || 0,
          CGST: formData.CGST,
          SGST: formData.SGST,
          IGST: formData.IGST,
          Roundoff: formData.Roundoff,
          TotalAmount: formData.TotalAmount,
          PaidAmount: formData.PaidAmount,
          CardAmount: parseFloat(formData.CardAmount) || 0,
          UpiAmount: parseFloat(formData.UpiAmount) || 0,
          BalanceAmount: formData.BalanceAmount,
          PaymentBankId: formData.PaymentBankId || null,
          IsType: false,
          IsActive: true,
          CreatedBy: userId,
          CreatedDate: today(),
          ModifiedBy: userId,
          ModifiedDate: today(),
        },
        PurchaseDetails: details.map((d, i) => ({
          Id: d.Id || 0,
          PurchaseId: purchaseData?.Id || 0,
          ItemId: d.ItemId,
          Quantity: parseInt(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          Discount: parseFloat(d.Discount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          GSTpercent: parseFloat(d.GSTpercent) || 0,
          CGST: parseFloat(d.CGST) || 0,
          SGST: parseFloat(d.SGST) || 0,
          IGST: parseFloat(d.IGST) || 0,
          CreatedBy: userId,
          CreatedDate: today(),
          ModifiedBy: userId,
          ModifiedDate: today(),
        })),
      };

      if (mode === 'edit' && purchaseData?.Id) {
        await updateData('PurchaseApi', purchaseData.Id, payload, token);
      } else {
        await postData('PurchaseApi', '', payload, token);
      }
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving purchase:', error);
      setErrors({ save: 'Failed to save purchase. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => parseFloat(v || 0).toFixed(2);

  // ─── Styles (same pattern as EstimateModal) ─────────────────────────────────
  const inp = { borderRadius: '4px', fontSize: '12px', height: '30px' };
  const lbl = { fontSize: '11px' };
  const amtStyle = { ...inp, backgroundColor: '#f0f7f0', fontWeight: '600', textAlign: 'right' };
  const footerInp = { borderRadius: '4px', fontSize: '12px', textAlign: 'right' };
  const footerAmt = { ...footerInp, backgroundColor: '#f0f7f0', fontWeight: '600' };
  const footerLbl = { fontSize: '11px' };

  const getTitle = () => ({ create: 'New Purchase', edit: 'Edit Purchase', view: 'Purchase Details' }[mode] || 'Purchase');

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullScreen={isMobile}
      PaperProps={{ style: { width: isMobile ? '100%' : '99vw', maxWidth: '99vw', borderRadius: isMobile ? '0' : '12px', background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', padding: '2px', margin: isMobile ? '0' : '4px' } }}>
      <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: isMobile ? '100vh' : 'auto', maxHeight: isMobile ? '100vh' : '95vh' }}>

        {/* ── Title Bar ── */}
        <DialogTitle style={{ background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', color: 'white', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto', flexShrink: 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>🛒 {getTitle()}</Typography>
            {formData.PurchaseBillNumber ? <Typography style={{ fontSize: '12px', opacity: 0.8 }}>#{formData.PurchaseBillNumber}</Typography> : null}
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        {/* ── Body ── */}
        <DialogContent style={{ padding: '10px 12px', flex: 1, overflow: 'auto' }}>
          {loading && <PurchaseLoader open={loading} message="Processing..." />}

        {/* ── VIEW MODE: Invoice style ── */}
        {isViewMode ? (
          <Box sx={{ background: 'white', borderRadius: '8px', p: '12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mb: '8px', pb: '8px', borderBottom: '2px solid #5c3d1e' }}>
              <Box>
                <Typography sx={{ fontSize: '11px', color: '#666' }}>Supplier</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>{formData.SupplierName || '—'}</Typography>
                <Typography sx={{ fontSize: '11px', color: '#555' }}>{formData.Address || ''}</Typography>
                {formData.GSTNumber && <Typography sx={{ fontSize: '11px', color: '#1976d2', fontWeight: '600' }}>GST: {formData.GSTNumber}</Typography>}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '11px', color: '#666' }}>Bill No</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#5c3d1e' }}>{formData.PurchaseBillNumber}</Typography>
                <Typography sx={{ fontSize: '11px', color: '#555' }}>Date: {formData.PurchaseDate ? new Date(formData.PurchaseDate).toLocaleDateString('en-IN') : '—'}</Typography>
              </Box>
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #ede4d4', borderRadius: '6px', mb: '10px' }}>
              <Table size="small" sx={{ '& td, & th': { py: '3px', px: '6px' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: '#f9f3e8', fontSize: '10px', fontWeight: '700', color: '#5c3d1e' } }}>
                    <TableCell>#</TableCell><TableCell>Item</TableCell><TableCell>HSN</TableCell><TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell align="center">Dis%</TableCell>
                    <TableCell align="center">GST%</TableCell><TableCell align="right">CGST</TableCell><TableCell align="right">SGST</TableCell>
                    <TableCell align="right">IGST</TableCell><TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((row, idx) => (
                    <TableRow key={idx} sx={{ '& td': { fontSize: '11px' }, background: idx % 2 === 0 ? '#fff' : '#fdf9f3' }}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: '600' }}>{row.ItemName || '—'}</TableCell>
                      <TableCell>{row.HSNCode || '—'}</TableCell>
                      <TableCell align="center">{row.Quantity}</TableCell>
                      <TableCell align="right">{fmt(row.Rate)}</TableCell>
                      <TableCell align="right">{fmt(row.Amount)}</TableCell>
                      <TableCell align="center">{row.Discount || 0}%</TableCell>
                      <TableCell align="center">{row.GSTpercent || 0}%</TableCell>
                      <TableCell align="right">{fmt(row.CGST)}</TableCell>
                      <TableCell align="right">{fmt(row.SGST)}</TableCell>
                      <TableCell align="right">{fmt(row.IGST)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: '700' }}>{fmt(row.TotalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ minWidth: '260px', background: '#f9f3e8', borderRadius: '6px', p: '8px' }}>
                {[
                  { lbl: 'Amount', val: formData.Amount },
                  { lbl: 'Discount', val: formData.DiscountAmount, color: '#d32f2f' },
                  { lbl: 'Other Amount', val: formData.OtherAmount },
                  { lbl: 'CGST', val: formData.CGST, color: '#1976d2' },
                  { lbl: 'SGST', val: formData.SGST, color: '#1976d2' },
                  { lbl: 'IGST', val: formData.IGST, color: '#1976d2' },
                  { lbl: 'Round Off', val: formData.Roundoff },
                ].map((r, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: '1px' }}>
                    <Typography sx={{ fontSize: '11px', color: '#555' }}>{r.lbl}</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: '600', color: r.color || '#333' }}>₹{fmt(r.val)}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '4px', mt: '4px', borderTop: '2px solid #5c3d1e' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: '800', color: '#5c3d1e' }}>Grand Total</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: '800', color: '#5c3d1e' }}>₹{fmt(formData.TotalAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '2px' }}>
                  <Typography sx={{ fontSize: '11px' }}>Paid</Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: '600', color: '#388e3c' }}>₹{fmt(formData.PaidAmount)}</Typography>
                </Box>
                {parseFloat(formData.CardAmount) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '2px' }}>
                    <Typography sx={{ fontSize: '11px' }}>Card</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: '600', color: '#1565c0' }}>₹{fmt(formData.CardAmount)}</Typography>
                  </Box>
                )}
                {parseFloat(formData.UpiAmount) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '2px' }}>
                    <Typography sx={{ fontSize: '11px' }}>UPI</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: '600', color: '#7b1fa2' }}>₹{fmt(formData.UpiAmount)}</Typography>
                  </Box>
                )}
                {formData.PaymentBankId && (parseFloat(formData.CardAmount) > 0 || parseFloat(formData.UpiAmount) > 0) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '2px' }}>
                    <Typography sx={{ fontSize: '11px', color: '#1565c0' }}>Bank</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: '600', color: '#1565c0' }}>{banks.find(b => b.Id === formData.PaymentBankId)?.Name || '—'}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '2px' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: '700', color: parseFloat(formData.BalanceAmount) > 0 ? '#d32f2f' : '#388e3c' }}>Balance</Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: '700', color: parseFloat(formData.BalanceAmount) > 0 ? '#d32f2f' : '#388e3c' }}>₹{fmt(formData.BalanceAmount)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
        /* ── CREATE / EDIT MODE (matches Estimate layout) ── */
        <>
          {/* ══ HEADER ══ */}
          <Grid container spacing={1}>
            {/* Row 1 */}
            <Grid item xs={3}>
              <Autocomplete options={suppliers} getOptionLabel={(o) => o.Name || ''}
                value={suppliers.find(s => s.Id === formData.SupplierId) || null}
                onChange={handleSupplierChange} disabled={isReadOnly} size="small"
                renderOption={(props, o) => <li {...props} key={o.Id} style={{ fontSize: '12px', padding: '4px 10px' }}><span style={{ fontWeight: '600' }}>{o.Name}</span></li>}
                renderInput={(p) => <TextField {...p} label="Supplier *" size="small" placeholder="Select supplier..." error={!!errors.SupplierId} InputProps={{ ...p.InputProps, style: inp }} InputLabelProps={{ style: lbl }} />}
                isOptionEqualToValue={(o, v) => o.Id === v?.Id}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', fontSize: '12px', height: '30px', padding: '0 8px !important' }, '& .MuiAutocomplete-endAdornment': { top: '4px' } }}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField label="Bill Date" type="date" value={formData.PurchaseDate} onChange={(e) => handleFieldChange('PurchaseDate', e.target.value)}
                fullWidth size="small" disabled={isReadOnly} error={!!errors.PurchaseDate}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl, shrink: true }} />
            </Grid>
            <Grid item xs={3}>
              <TextField label="Bill No *" value={formData.PurchaseBillNumber} onChange={(e) => handleFieldChange('PurchaseBillNumber', e.target.value)}
                fullWidth size="small" disabled={isReadOnly} error={!!errors.PurchaseBillNumber}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }} />
            </Grid>
            <Grid item xs={3}>
              <TextField label="GST Number" value={formData.GSTNumber} onChange={(e) => handleFieldChange('GSTNumber', e.target.value)}
                fullWidth size="small" disabled={isReadOnly}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }} />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={3}>
              <TextField label="Address" value={formData.Address} onChange={(e) => handleFieldChange('Address', e.target.value)}
                fullWidth size="small" disabled={isReadOnly} inputProps={{ autoComplete: 'off' }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }} />
            </Grid>
            <Grid item xs={9}>
              <TextField label="Remarks" value={formData.Description || formData.Remarks} onChange={(e) => handleFieldChange('Description', e.target.value)}
                fullWidth size="small" disabled={isReadOnly} inputProps={{ autoComplete: 'off' }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }} />
            </Grid>
          </Grid>

          {/* ══ PRODUCT DETAILS ══ */}
          <Box sx={{ mt: '10px', border: '1px solid #c9a84c', borderRadius: '6px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#3b2410', px: '10px', py: '4px' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: '700', color: 'white', textTransform: 'uppercase' }}>Product Details</Typography>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: '14px' }} />} onClick={handleAddRow}
                sx={{ fontSize: '11px', textTransform: 'none', color: 'white', fontWeight: '600', background: '#4CAF50', borderRadius: '4px', px: 1, py: '2px', minHeight: '22px', '&:hover': { background: '#388e3c' } }}>
                Product
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: '300px' }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& td, & th': { py: '3px', px: '6px', fontSize: '11px' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: '#f5f0e6', fontWeight: '700', color: '#3b2410', textTransform: 'uppercase', fontSize: '10px', whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: '18%' }}>Item</TableCell>
                    <TableCell sx={{ width: '8%' }}>HSN</TableCell>
                    <TableCell align="center" sx={{ width: '7%' }}>Qty</TableCell>
                    <TableCell align="right" sx={{ width: '9%' }}>Rate</TableCell>
                    <TableCell align="right" sx={{ width: '10%' }}>Amt</TableCell>
                    <TableCell align="center" sx={{ width: '7%' }}>Dis%</TableCell>
                    <TableCell align="center" sx={{ width: '7%' }}>GST%</TableCell>
                    <TableCell align="right" sx={{ width: '9%' }}>CGST</TableCell>
                    <TableCell align="right" sx={{ width: '9%' }}>SGST</TableCell>
                    <TableCell align="right" sx={{ width: '9%' }}>IGST</TableCell>
                    <TableCell align="right" sx={{ width: '10%' }}>Total</TableCell>
                    <TableCell sx={{ width: '3%' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.length === 0 ? (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 2, color: '#999' }}>No products added. Click "+ Product" to add</TableCell></TableRow>
                  ) : details.map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:nth-of-type(even)': { background: '#fdf9f3' } }}>
                      <TableCell>
                        <Autocomplete options={items} getOptionLabel={(o) => o.Name || o.ItemName || ''}
                          value={items.find(i => i.Id === row.ItemId) || null}
                          onChange={(_, val) => handleItemSelect(idx, val)} size="small"
                          renderOption={(props, o) => <li {...props} key={o.Id} style={{ fontSize: '11px', padding: '3px 8px', display: 'flex', justifyContent: 'space-between' }}><span>{o.Name}</span><span style={{ color: '#888', fontSize: '10px' }}>₹{o.PurchaseRate || 0} | {o.GSTpercent || 0}%</span></li>}
                          renderInput={(p) => <TextField {...p} variant="standard" placeholder="Select item" sx={{ '& input': { fontSize: '11px' } }} />}
                          isOptionEqualToValue={(o, v) => o.Id === v?.Id} />
                      </TableCell>
                      <TableCell><TextField variant="standard" size="small" value={row.HSNCode} disabled sx={{ '& input': { fontSize: '11px' } }} /></TableCell>
                      <TableCell align="center"><TextField variant="standard" size="small" type="number" value={row.Quantity} onChange={(e) => handleDetailChange(idx, 'Quantity', e.target.value)} sx={{ '& input': { fontSize: '11px', textAlign: 'center' } }} /></TableCell>
                      <TableCell align="right"><TextField variant="standard" size="small" type="number" value={row.Rate} onChange={(e) => handleDetailChange(idx, 'Rate', e.target.value)} sx={{ '& input': { fontSize: '11px', textAlign: 'right' } }} /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: '600' }}>{fmt(row.Amount)}</TableCell>
                      <TableCell align="center"><TextField variant="standard" size="small" type="number" value={row.Discount} onChange={(e) => handleDetailChange(idx, 'Discount', e.target.value)} sx={{ '& input': { fontSize: '11px', textAlign: 'center' } }} /></TableCell>
                      <TableCell align="center"><TextField variant="standard" size="small" type="number" value={row.GSTpercent} onChange={(e) => handleDetailChange(idx, 'GSTpercent', e.target.value)} sx={{ '& input': { fontSize: '11px', textAlign: 'center' } }} /></TableCell>
                      <TableCell align="right">{fmt(row.CGST)}</TableCell>
                      <TableCell align="right">{fmt(row.SGST)}</TableCell>
                      <TableCell align="right">{fmt(row.IGST)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: '700', color: '#3b2410' }}>{fmt(row.TotalAmount)}</TableCell>
                      <TableCell><IconButton size="small" onClick={() => handleRemoveRow(idx)} sx={{ color: '#d32f2f', p: '2px' }}><DeleteIcon sx={{ fontSize: '16px' }} /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* ══ FOOTER AMOUNTS ══ */}
          <Grid container spacing={1} sx={{ mt: '8px' }}>
            <Grid item xs><TextField label="Amount" value={fmt(formData.Amount)} fullWidth size="small" disabled InputProps={{ style: amtStyle }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Discount" value={fmt(formData.DiscountAmount)} fullWidth size="small" disabled InputProps={{ style: amtStyle }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Other Amt" value={formData.OtherAmount} onChange={(e) => handleFieldChange('OtherAmount', parseFloat(e.target.value) || 0)} type="number" fullWidth size="small" InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="CGST" value={fmt(formData.CGST)} fullWidth size="small" disabled InputProps={{ style: amtStyle }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="SGST" value={fmt(formData.SGST)} fullWidth size="small" disabled InputProps={{ style: amtStyle }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="IGST" value={fmt(formData.IGST)} fullWidth size="small" disabled InputProps={{ style: amtStyle }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Round Off" value={fmt(formData.Roundoff)} fullWidth size="small" disabled InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Total" value={fmt(formData.TotalAmount)} fullWidth size="small" disabled InputProps={{ style: { ...footerAmt, fontSize: '13px' } }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Cash Paid" value={formData.PaidAmount} onChange={(e) => handleFieldChange('PaidAmount', e.target.value)} type="number" fullWidth size="small" InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl }} /></Grid>
            <Grid item xs><TextField label="Card Amount" value={formData.CardAmount} onChange={(e) => handleFieldChange('CardAmount', e.target.value)} type="number" fullWidth size="small" InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} inputProps={{ step: '0.01', style: { textAlign: 'right' } }} /></Grid>
            <Grid item xs><TextField label="UPI Amount" value={formData.UpiAmount} onChange={(e) => handleFieldChange('UpiAmount', e.target.value)} type="number" fullWidth size="small" InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} inputProps={{ step: '0.01', style: { textAlign: 'right' } }} /></Grid>
            {(parseFloat(formData.CardAmount) > 0 || parseFloat(formData.UpiAmount) > 0) && (
              <Grid item xs sx={{ minWidth: '120px' }}>
                <TextField
                  select
                  label="Bank"
                  value={formData.PaymentBankId ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, PaymentBankId: e.target.value ? parseInt(e.target.value) : null }))}
                  fullWidth size="small"
                  InputProps={{ style: { ...footerInp, backgroundColor: '#e3f2fd' } }}
                  InputLabelProps={{ style: { ...footerLbl, color: '#1565c0' }, shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1976d2' }, '&:hover fieldset': { borderColor: '#1565c0' }, '&.Mui-focused fieldset': { borderColor: '#0d47a1' } } }}
                >
                  <MenuItem value=""><em>— Select Bank —</em></MenuItem>
                  {banks.map(b => (
                    <MenuItem key={b.Id} value={b.Id}>{b.Name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs><TextField label="Balance" value={fmt(formData.BalanceAmount)} fullWidth size="small" disabled InputProps={{ style: { ...footerAmt, backgroundColor: parseFloat(formData.BalanceAmount) > 0 ? '#fff0f0' : '#f0f7f0' } }} InputLabelProps={{ style: footerLbl }} /></Grid>
          </Grid>

          {errors.save && <Typography sx={{ color: '#d32f2f', fontSize: '11px', mt: '4px', textAlign: 'center' }}>{errors.save}</Typography>}
        </>
        )}
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions style={{ padding: '6px 12px', borderTop: '1px solid #e0d6c8', background: '#fdf6ec', flexShrink: 0 }}>
          <Button onClick={onClose} style={{ background: 'linear-gradient(135deg, #666 0%, #888 100%)', color: 'white', borderRadius: '6px', padding: '4px 14px', fontSize: '12px', fontWeight: '600', textTransform: 'none', minHeight: '30px' }}>
            Cancel
          </Button>
          {!isViewMode && (
            <Button onClick={handleSave} disabled={loading} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: '6px', padding: '4px 14px', fontSize: '12px', fontWeight: '600', textTransform: 'none', minHeight: '30px' }}>
              {loading ? <CircularProgress size={14} style={{ color: '#fff' }} /> : (mode === 'edit' ? 'Update Purchase' : 'Save Purchase')}
            </Button>
          )}
        </DialogActions>

      </Box>
    </Dialog>
  );
};

export default PurchaseModal;
